import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { SegmentedChoice } from '../../../../components/design-system/common';

import { StudioShot } from '../studio-contract';
import { cameraLabel } from '../labels';
import { TextPreview } from './TextPreview';
import { photoIsDesigned } from '../studio-contract';
import { SELECTED_FRAME } from '../../../../components/design-system/common/selection';

interface ShotPreviewProps {
    shots: StudioShot[];
    /** Index of the shot shown, or null when closed. */
    index: number | null;
    onIndex: (i: number | null) => void;
    /** Put an earlier take back on the card. */
    onUseTake: (shotId: string, stage: 'still' | 'clip', assetUuid: string) => void;
}

/** Shots that have something to look at. */
export function previewable(s: StudioShot): boolean {
    return (s.still?.status === 'ready' && !!s.still.url) || (s.clip?.status === 'ready' && !!s.clip.url);
}

/**
 * A shot at full size: the film if there is one, the photo otherwise, with the
 * earlier takes beside it. Arrows (and the keyboard) step through the board,
 * skipping shots with nothing made yet.
 */
export const ShotPreview: React.FC<ShotPreviewProps> = ({ shots, index, onIndex, onUseTake }) => {
    const { t } = useTranslation();
    const shot = index != null ? shots[index] : null;
    const hasClip = !!shot && shot.clip?.status === 'ready' && !!shot.clip.url;
    const [view, setView] = useState<'clip' | 'still'>('clip');
    const [take, setTake] = useState<string | null>(null);
    const [withText, setWithText] = useState(true);

    useEffect(() => { setView(hasClip ? 'clip' : 'still'); setTake(null); }, [index, hasClip]);

    const step = (dir: 1 | -1) => {
        if (index == null) return;
        for (let i = index + dir; i >= 0 && i < shots.length; i += dir) {
            if (previewable(shots[i])) { onIndex(i); return; }
        }
    };

    useEffect(() => {
        if (index == null) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') step(1);
            if (e.key === 'ArrowLeft') step(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index, shots]);

    if (!shot) return null;
    const showClip = view === 'clip' && hasClip && !take;
    // Which stage's takes are listed: the film's while looking at the film.
    const showClipStage = view === 'clip' && hasClip;
    const takes = (showClipStage ? shot.clip?.takes : shot.still?.takes) || [];
    const url = take || (showClip ? shot.clip!.url! : shot.still?.url);
    const isVideo = showClip || (!!take && /\.(mp4|webm|mov)(\?|$)/i.test(take));

    return (
        <Dialog open onOpenChange={(o) => !o && onIndex(null)}>
            <DialogContent className="flex h-[92vh] max-w-[min(1200px,96vw)] flex-col gap-3 p-4 sm:max-w-[min(1200px,96vw)]">
                <div className="flex items-center gap-3 pr-8">
                    <DialogTitle className="min-w-0 truncate text-sm font-medium">
                        {index! + 1}. {shot.title || cameraLabel(t, shot.camera).label}
                    </DialogTitle>
                    {hasClip && (
                        <div className="w-44">
                            <SegmentedChoice
                                value={view}
                                onChange={(v) => { setView(v); setTake(null); }}
                                options={[{ value: 'clip', label: t('STUDIO.PREVIEW.FILM', 'Film') }, { value: 'still', label: t('STUDIO.PREVIEW.PHOTO', 'Photo') }]}
                            />
                        </div>
                    )}
                    {shot.text?.content && (isVideo || !photoIsDesigned(shot)) && (
                        <Button variant="ghost" size="sm" onClick={() => setWithText((v) => !v)} className="text-muted-foreground">
                            {withText ? t('STUDIO.PREVIEW.HIDE_TEXT', 'Hide text') : t('STUDIO.PREVIEW.SHOW_TEXT', 'Show text')}
                        </Button>
                    )}
                    <div className="ml-auto flex items-center gap-1">
                        {take && (
                            <Button variant="outline" size="sm" onClick={() => {
                                const stage = showClipStage ? 'clip' : 'still';
                                const hit = (shot[stage]?.takes || []).find((x) => x.url === take);
                                if (hit) { onUseTake(shot.id, stage, hit.assetUuid); setTake(null); }
                            }}>
                                {t('STUDIO.PREVIEW.USE_TAKE', 'Use this take')}
                            </Button>
                        )}
                        {url && (
                            <Button variant="outline" size="sm" asChild className="gap-1.5">
                                <a href={url} download target="_blank" rel="noreferrer"><Download className="size-3.5" />{t('STUDIO.DOWNLOAD', 'Download')}</a>
                            </Button>
                        )}
                    </div>
                </div>

                <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg bg-muted/40">
                    <Button variant="ghost" size="icon" className="absolute left-2 top-1/2 z-10 -translate-y-1/2" onClick={() => step(-1)} aria-label={t('STUDIO.PREVIEW.PREV', 'Previous shot')}>
                        <ChevronLeft className="size-5" />
                    </Button>
                    {url && (
                        <div className="relative max-h-full max-w-full">
                            {isVideo
                                ? <video key={url} src={url} poster={shot.still?.url} controls autoPlay loop playsInline className="block max-w-full rounded-md" style={{ maxHeight: 'calc(92vh - 150px)' }} />
                                : <img key={url} src={url} alt={shot.title || ''} className="block max-w-full rounded-md object-contain" style={{ maxHeight: 'calc(92vh - 150px)' }} />}
                            {withText && shot.text?.content && !take && (isVideo || !photoIsDesigned(shot)) && <TextPreview text={shot.text} />}
                        </div>
                    )}
                    <Button variant="ghost" size="icon" className="absolute right-2 top-1/2 z-10 -translate-y-1/2" onClick={() => step(1)} aria-label={t('STUDIO.PREVIEW.NEXT', 'Next shot')}>
                        <ChevronRight className="size-5" />
                    </Button>
                </div>

                {takes.length > 0 && (
                    <div className="flex items-center gap-2 overflow-x-auto">
                        <span className="shrink-0 text-xs text-muted-foreground">{t('STUDIO.PREVIEW.TAKES', 'Earlier takes')}</span>
                        {[{ url: showClipStage ? shot.clip!.url! : shot.still!.url!, current: true }, ...takes.map((x) => ({ url: x.url, current: false }))].map((x, i) => (
                            <button key={x.url + i} type="button" onClick={() => setTake(x.current ? null : x.url)}
                                className={cn('h-14 shrink-0 cursor-pointer overflow-hidden rounded-md border', (take ? take === x.url : x.current) ? SELECTED_FRAME : 'border-input')}>
                                {/\.(mp4|webm|mov)(\?|$)/i.test(x.url)
                                    ? <video src={x.url} muted className="h-full w-auto" />
                                    : <img src={x.url} alt="" className="h-full w-auto" />}
                            </button>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
